import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import {
  ClientGroup, ClientGroupMember, ClientGroupMemberState, InviteClientUserPayload, UpdateClientUserPayload
} from '../interfaces/client-group.interface';

const PENDING_EMAIL_VERIFICATION = 'PENDING_EMAIL_VERIFICATION';

/**
 * Pill state for a client user (MP-5713): deactivated wins over everything,
 * then invited while the set-password email is unanswered, else active.
 */
export function clientGroupMemberState(member: Pick<ClientGroupMember, 'isActive' | 'status'>): ClientGroupMemberState {
  if (!member.isActive) {
    return 'deactivated';
  }
  return member.status === PENDING_EMAIL_VERIFICATION ? 'invited' : 'active';
}

/**
 * Client groups API (MP-5715 over the MP-5712 / MP-5713 backend resource).
 *
 * The backend scopes every call to the requester's firm and authorizes
 * FIRM_MANAGER / PROJECT_OWNER itself, so no firm id travels in the request.
 * The whole resource answers 404 for tenants without the clientPortal
 * capability; callers hide the UI on that flag rather than probing for it.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientGroupService {

  constructor(private http: HttpBaseService) { }

  /** Active groups of the requester's firm; `includeInactive` adds deactivated ones. */
  getGroups(includeInactive = false): Observable<ClientGroup[]> {
    const options = includeInactive ? { params: new HttpParams().set('includeInactive', 'true') } : undefined;
    return this.http.get('clientGroups', options);
  }

  createGroup(name: string): Observable<ClientGroup> {
    return this.http.post('clientGroups', { name });
  }

  renameGroup(groupId: string, name: string): Observable<ClientGroup> {
    return this.http.put(`clientGroups/${groupId}`, { name });
  }

  /** Soft deactivate; there is no hard delete and no reactivate endpoint yet. */
  deactivateGroup(groupId: string): Observable<ClientGroup> {
    return this.http.put(`clientGroups/${groupId}/deactivate`);
  }

  /** Client users of a group, sorted by name on the backend. */
  getMembers(groupId: string): Observable<ClientGroupMember[]> {
    return this.http.get(`clientGroups/${groupId}/users`);
  }

  /** Creates the CLIENT user in the group and sends the set-password invite email. */
  inviteMember(groupId: string, payload: InviteClientUserPayload): Observable<ClientGroupMember> {
    return this.http.post(`clientGroups/${groupId}/users`, payload);
  }

  /** Only for members still in PENDING_EMAIL_VERIFICATION; the backend rejects the rest with 400. */
  resendInvite(groupId: string, userId: string): Observable<{ success: boolean }> {
    return this.http.post(`clientGroups/${groupId}/users/${userId}/resend`);
  }

  /** Name and phone only; email and role are not editable. */
  updateMember(groupId: string, userId: string, payload: UpdateClientUserPayload): Observable<ClientGroupMember> {
    return this.http.put(`clientGroups/${groupId}/users/${userId}`, payload);
  }

  /** Deactivation locks the user out at the token layer on their next request. */
  setMemberStatus(groupId: string, userId: string, isActive: boolean): Observable<ClientGroupMember> {
    return this.http.put(`clientGroups/${groupId}/users/${userId}/status`, { isActive });
  }
}
